import React, { useMemo } from "react";
import { NavLink } from "react-router-dom";
import EmblemImage from "../../EmblemImage";
import RoundedRectangle from "../../RoundedRectangle";
import { MatchSmallElement } from "../../../../config/types";
import { pathMatchView } from "../../../../config/routes";
import ScaleFade from "../../Animated/ScaleFade";
import { toMs } from "../../../../config/utils";

const TeamLastMatches: React.FC<{
  teamId?: number;
  matches?: MatchSmallElement[];
}> = ({ teamId, matches }) => {
  const lastFiveOpponents = useMemo(() => {
    if (!matches || !teamId)
      return [] as Array<{
        key: string;
        matchId: number;
        emblemUrl?: string;
        score: string;
        outcome: "win" | "draw" | "loss";
      }>;
    const now = Date.now();
    const past = matches
      .filter((m) => {
        const endMs = toMs(m.endTime);
        return Number.isFinite(endMs) && endMs <= now;
      })
      .sort((a, b) => toMs(b.startTime) - toMs(a.startTime))
      .slice(0, 5);
    return past.map((m) => {
      const isTeam1 = m.team1.id === teamId;
      const opponent = isTeam1 ? m.team2 : m.team1;
      const my = isTeam1 ? m.goal1 : m.goal2;
      const opp = isTeam1 ? m.goal2 : m.goal1;
      const outcome: "win" | "draw" | "loss" =
        my > opp ? "win" : my < opp ? "loss" : "draw";
      const score = `${m.goal1}-${m.goal2}`;
      return {
        key: `opp-${m.id}-${opponent.id}`,
        matchId: m.id,
        emblemUrl: opponent.emblemUrl,
        score,
        outcome,
      };
    });
  }, [matches, teamId]);
  return (
    <RoundedRectangle
      className="w-full bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark"
      hasBorder
      hasTitle
      titleType="upLeft"
      titleKey="lastMatches"
    >
      <div
        className={`tablet-panel-body mt-4 w-full flex flex-row items-end ${
          lastFiveOpponents.length === 5
            ? "justify-between"
            : "justify-start gap-[calc(calc(100%-calc(64px*5))/4)] "
        }`}
      >
        {[...lastFiveOpponents].reverse().map((opp) => (
          <NavLink
            key={opp.key}
            to={pathMatchView(opp.matchId, "highlights")}
            className="no-underline"
          >
            <div className="flex flex-col items-center gap-2 w-16">
              <span
                className={`${
                  opp.outcome === "win"
                    ? "bg-green"
                    : opp.outcome === "draw"
                    ? "bg-grey"
                    : "bg-red"
                } text-white rounded-lg inline-flex items-center justify-center h-6 w-12 text-xs text-center font-medium`}
              >
                {opp.score}
              </span>
              <div className="w-8 h-8 flex items-center justify-center">
                <ScaleFade isOpen hover>
                  <EmblemImage imageUrl={opp.emblemUrl} width={32} />
                </ScaleFade>
              </div>
            </div>
          </NavLink>
        ))}
      </div>
    </RoundedRectangle>
  );
};

export default TeamLastMatches;
